import {
  AlertTriangle,
  ArrowRight,
  ArrowRightLeft,
  Backpack,
  Brain,
  ChevronDown,
  Heart,
  HeartPulse,
  History,
  Link2,
  MapPinned,
  Moon,
  Sparkles,
  UserRound,
  UserPlus,
  Utensils,
  Wrench,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { formatDuration, formatQuantity } from "../lib/format";
import {
  coverageImpactWarning,
  isSurvivorAvailableForTemporaryWork,
  resolvedSurvivorActivity,
  roomName,
  survivorActivityLabel,
  survivorDutySpaceId,
} from "../lib/scheduling";
import { resolveSurvivorPresentation } from "../lib/survivorMotion";
import { useResponsiveLayout } from "../lib/useResponsiveLayout";
import type { Catalog, GameCommand, GameState, SkillKey, Survivor } from "../types";
import { StatusDial } from "./StatusDial";
import { SurvivorFigure } from "./SurvivorFigure";
import { SurvivorPortrait } from "./SurvivorPortrait";

const skillLabels: Record<SkillKey, string> = {
  medicine: "Medicine",
  combat: "Combat",
  scouting: "Scouting",
  cooking: "Cooking",
  engineering: "Engineering",
  logistics: "Logistics",
  crafting: "Crafting",
};

function conditionBand(condition: number): { label: string; className: string; effect: string } {
  if (condition < 25) return { label: "Broken", className: "broken", effect: "Skill bonuses disabled" };
  if (condition < 60) return { label: "Worn", className: "worn", effect: "Skill bonuses reduced" };
  if (condition < 90) return { label: "Serviceable", className: "serviceable", effect: "Full skill bonus" };
  return { label: "Maintained", className: "maintained", effect: "Full skill bonus" };
}

function EquipmentManager({
  survivor,
  state,
  catalog,
  busy,
  onCommand,
}: {
  survivor: Survivor;
  state: GameState;
  catalog: Catalog;
  busy: boolean;
  onCommand: (command: GameCommand) => Promise<unknown>;
}) {
  if (!survivor.equipment.length) {
    return <p className="quiet-state">No personal equipment assigned.</p>;
  }
  const workshops = state.site.spaces.filter((space) => (
    space.room?.type === "workshop"
    && space.room.status === "operational"
    && space.room.enabled !== false
  ));
  const workshop = workshops.find((space) => (
    !state.tasks.some((task) => task.space_id === space.id)
  ));
  const transferTargets = state.survivors.filter((person) => (
    person.id !== survivor.id && person.alive && person.status === "home"
  ));
  const bestWorker = state.survivors
    .filter(isSurvivorAvailableForTemporaryWork)
    .sort((left, right) => (
      Number(Boolean(coverageImpactWarning(state, left))) - Number(Boolean(coverageImpactWarning(state, right)))
      || right.skills.crafting - left.skills.crafting
      || left.id.localeCompare(right.id)
    ))[0];
  const serviceCoverageWarning = bestWorker ? coverageImpactWarning(state, bestWorker) : null;

  return (
    <div className="equipment-list equipment-management">
      {survivor.equipment.map((item) => {
        const definition = catalog.equipment[item];
        const name = definition?.name ?? item.replaceAll("_", " ");
        const condition = Math.max(0, Math.min(100, survivor.equipment_condition?.[item] ?? 100));
        const band = conditionBand(condition);
        const unavailable = survivor.equipment_unavailable?.includes(item) ?? false;
        const servicing = unavailable || state.tasks.some((task) => (
          task.kind === "service_equipment"
          && task.equipment_owner_id === survivor.id
          && task.equipment_item_id === item
        ));
        const missing = 100 - condition;
        const serviceCost = {
          scrap: Math.max(20, missing * 2),
          components: Math.max(10, missing),
        };
        const workshopBusy = workshops.length > 0 && !workshop;
        const hasMaterials = (
          (state.resources?.scrap ?? 0) >= serviceCost.scrap
          && (state.resources?.components ?? 0) >= serviceCost.components
        );
        const serviceBlockedReason = servicing
          ? "Service underway"
          : condition >= 100
            ? "Fully serviced"
            : survivor.status !== "home"
              ? "Owner is away"
              : workshops.length === 0
                ? "Workshop required"
                : workshopBusy
                  ? "Workshop busy"
                  : !bestWorker
                    ? "Free worker required"
                    : !hasMaterials
                      ? "Materials required"
                      : null;
        return (
          <article className={`equipment-card condition-${band.className}`} key={item}>
            <header>
              <span><b>{name}</b><small>{definition?.description ?? "Personal gear carried by this survivor."}</small></span>
              <em>{condition}% · {band.label}</em>
            </header>
            <div className="equipment-condition" aria-label={`${name} condition ${condition}%`}>
              <i><b style={{ width: `${condition}%` }} /></i>
              <small>{unavailable ? "Unavailable while stripped down in the workshop" : band.effect}</small>
            </div>
            <div className="equipment-actions">
              <label>
                <ArrowRightLeft aria-hidden="true" />
                <span className="visually-hidden">Transfer {name}</span>
                <select
                  aria-label={`Transfer ${name}`}
                  disabled={busy || survivor.status !== "home" || !transferTargets.length || servicing}
                  value=""
                  onChange={(event) => {
                    if (!event.currentTarget.value) return;
                    void onCommand({
                      type: "transfer_equipment",
                      from_survivor_id: survivor.id,
                      to_survivor_id: event.currentTarget.value,
                      item_id: item,
                    });
                  }}
                >
                  <option value="">Pass to…</option>
                  {transferTargets.map((person) => <option value={person.id} key={person.id}>{person.name}</option>)}
                </select>
              </label>
              <button
                type="button"
                disabled={busy || serviceBlockedReason !== null}
                title={serviceCoverageWarning ?? undefined}
                onClick={() => void onCommand({
                  type: "service_equipment",
                  survivor_id: survivor.id,
                  item_id: item,
                  worker_ids: [bestWorker!.id],
                })}
              >
                <Wrench aria-hidden="true" />
                {serviceBlockedReason ?? "Service gear"}
              </button>
            </div>
            {condition < 100 && !servicing && (
              <small className="equipment-service-cost">
                Service cost · {formatQuantity(serviceCost.scrap, catalog.quantity_scale)} scrap · {formatQuantity(serviceCost.components, catalog.quantity_scale)} components
              </small>
            )}
            {condition < 100 && !servicing && !serviceBlockedReason && serviceCoverageWarning && (
              <small className="equipment-service-warning"><AlertTriangle aria-hidden="true" />{serviceCoverageWarning}</small>
            )}
          </article>
        );
      })}
    </div>
  );
}

interface PeoplePanelProps {
  state: GameState;
  catalog: Catalog;
  selectedId: string | null;
  busy: boolean;
  onSelect: (id: string) => void;
  onCommand: (command: GameCommand) => Promise<unknown>;
  onOpenSurface?: () => void;
  onOpenInfirmary?: (spaceId: string) => void;
}

export function PeoplePanel({ state, catalog, selectedId, busy, onSelect, onCommand, onOpenSurface, onOpenInfirmary }: PeoplePanelProps) {
  const layout = useResponsiveLayout();
  const [chooserOpen, setChooserOpen] = useState(false);
  const [filter, setFilter] = useState<"active" | "away" | "deceased">("active");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"name" | "health" | "skill">("name");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  useEffect(() => setPage(1), [filter, search, sort]);
  const survivorsById = useMemo(
    () => new Map(state.survivors.map((person) => [person.id, person])),
    [state.survivors],
  );
  const filteredPeople = useMemo(() => state.survivors
    .filter((person) => {
      if (filter === "deceased") return !person.alive;
      if (filter === "away") return person.alive && person.status === "away";
      return person.alive && person.status === "home";
    })
    .filter((person) => `${person.name} ${person.role} ${person.traits.join(" ")}`.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((left, right) => {
      if (sort === "health") return right.health - left.health || left.name.localeCompare(right.name);
      if (sort === "skill") {
        const leftBest = Math.max(...Object.values(left.skills));
        const rightBest = Math.max(...Object.values(right.skills));
        return rightBest - leftBest || left.name.localeCompare(right.name);
      }
      return left.name.localeCompare(right.name);
    }), [filter, search, sort, state.survivors]);
  const totalPages = Math.max(1, Math.ceil(filteredPeople.length / pageSize));
  const visiblePeople = filteredPeople.slice((page - 1) * pageSize, page * pageSize);
  const survivor = state.survivors.find((item) => item.id === selectedId) ?? state.survivors[0];
  if (!survivor) return null;
  const presentation = resolveSurvivorPresentation(state, survivor);
  const activity = resolvedSurvivorActivity(survivor);
  const activityLabel = survivorActivityLabel(state, survivor);
  const dutyName = roomName(state, survivorDutySpaceId(survivor)) ?? "No preferred duty";
  const workPolicy = state.work_policy ?? "normal";
  const reliefEligible = survivor.relief_eligible ?? true;
  const ActivityIcon = activity.kind === "treatment"
    ? HeartPulse
    : activity.kind === "rest"
      ? Moon
      : activity.kind === "relief"
        ? ArrowRightLeft
        : Wrench;
  const connections = state.relationships
    .filter((relationship) => relationship.survivor_ids.includes(survivor.id))
    .map((relationship) => ({
      ...relationship,
      other: survivorsById.get(
        relationship.survivor_ids[0] === survivor.id
          ? relationship.survivor_ids[1]
          : relationship.survivor_ids[0],
      ),
    }))
    .sort((left, right) => Math.max(right.bond, right.tension) - Math.max(left.bond, left.tension) || right.bond - left.bond)
    .slice(0, 6);
  const memories = connections
    .flatMap((connection) => connection.memories.map((memory) => ({ ...memory, connection: connection.other?.name })))
    .sort((left, right) => right.game_time - left.game_time)
    .slice(0, 4);
  const needsTreatment = survivor.alive && (
    survivor.health < 100 || survivor.injuries.some((injury) => injury.severity > 0)
  );
  const infirmaries = state.site.spaces.filter(
    (space) => space.room?.type === "infirmary",
  );
  const activityFacility = activity.kind === "treatment"
    ? infirmaries.find((space) => space.id === activity.facility_space_id)
    : undefined;
  const infirmary = activityFacility ?? infirmaries.find(
    (space) =>
      space.room?.status === "operational"
      && space.room.enabled !== false
      && space.room.operation?.blocked_reason === null
      && (space.room.operation.treatment?.care_capacity_per_hour ?? 0) > 0,
  ) ?? infirmaries.find(
    (space) =>
      space.room?.status === "operational"
      && space.room.enabled !== false,
  ) ?? infirmaries.find(
    (space) => space.room?.status === "operational",
  ) ?? infirmaries[0];
  const treatmentBlocker = infirmary?.room?.operation?.blocked_reason;
  const treatmentGuidance = survivor.status === "away"
    ? "They cannot be treated while away. Infirmary care begins automatically after they return home."
    : !infirmary
      ? "Healing is automatic in an operational Infirmary. Build one in an excavated chamber, keep it powered, ensure a primary or relief medic is active, and stock Medicine."
      : infirmary.room?.status !== "operational"
        ? "The Infirmary must be repaired before automatic treatment can resume."
        : treatmentBlocker === "unstaffed"
          ? "Automatic treatment is waiting for an active primary or relief medic. Medicine skill improves throughput."
          : treatmentBlocker === "no_medicine"
            ? "Automatic treatment is waiting for Medicine. Each care point consumes 0.05."
            : treatmentBlocker === "load_shed" || treatmentBlocker === "no_power"
              ? "Automatic treatment is paused until the Infirmary receives power."
              : treatmentBlocker === "disabled"
                ? "Automatic treatment is paused because the Infirmary is switched off."
                : "They are in the automatic Infirmary queue. Keep an active primary or relief medic and Medicine stocked while care continues.";
  const population = state.population ?? {
    living: state.survivors.filter((person) => person.alive).length,
    home: state.survivors.filter((person) => person.alive && person.status === "home").length,
    away: state.survivors.filter((person) => person.alive && person.status === "away").length,
    deceased: state.survivors.filter((person) => !person.alive).length,
    bunk_capacity: 0,
    unhoused_count: 0,
    idle_count: state.survivors.filter((person) => person.alive && person.status === "home" && !person.assignment).length,
    food_upkeep_per_hour: 0,
    water_upkeep_per_hour: 0,
  };
  const legacyFiniteRecruitment = state.recruitment?.limit != null;
  const recruitmentAvailable = state.recruitment?.available ?? ((state.recruitment?.remaining ?? 1) > 0);

  return (
    <section className="people-layout" aria-busy={busy}>
      <details
        className="people-list panel-surface"
        onToggle={(event) => {
          if (layout.narrow) setChooserOpen(event.currentTarget.open);
        }}
        open={!layout.narrow || chooserOpen}
      >
        <summary className="mobile-picker-summary">
          <span><small>Selected survivor</small><strong>{layout.narrow ? survivor.name : "Survivor chooser"}</strong></span>
          <span>Choose survivor</span>
          <ChevronDown aria-hidden="true" />
        </summary>
        <div className="people-list-content">
        <div className="section-heading"><span className="eyebrow">The community</span><h2>{population.living} people remain</h2></div>
        <section className="population-summary" aria-label="Community capacity">
          <span><small>Home / away</small><strong>{population.home} / {population.away}</strong></span>
          <span className={population.unhoused_count ? "warning" : ""}><small>Bunks</small><strong>{population.home - population.unhoused_count}/{population.bunk_capacity}</strong></span>
          <span><small>Unhoused</small><strong>{population.unhoused_count}</strong></span>
          <span><small>Idle</small><strong>{population.idle_count}</strong></span>
          <span><small>Hourly upkeep</small><strong>{formatQuantity(population.food_upkeep_per_hour)} food · {formatQuantity(population.water_upkeep_per_hour)} water</strong></span>
        </section>
        <section className="work-policy-control" aria-label="Community work and rest policy">
          <header><Moon aria-hidden="true" /><span><small>Work and rest</small><strong>{workPolicy} policy</strong></span></header>
          <p>People pause their duties automatically, recover, then return. This policy controls when they stop and resume.</p>
          <div>
            {([
              ["normal", "Normal", "Rest 60 → 25"],
              ["extended", "Extended", "Rest 75 → 35"],
              ["emergency", "Emergency", "Rest 90 → 50"],
            ] as const).map(([policy, label, threshold]) => (
              <button
                type="button"
                aria-pressed={workPolicy === policy}
                disabled={busy || workPolicy === policy}
                key={policy}
                onClick={() => onCommand({ type: "set_work_policy", policy })}
              >
                <strong>{label}</strong><small>{threshold}</small>
              </button>
            ))}
          </div>
        </section>
        <div className="people-list-tools">
          <div role="group" aria-label="Filter survivors">
            {(["active", "away", "deceased"] as const).map((value) => (
              <button type="button" aria-pressed={filter === value} className={filter === value ? "active" : ""} key={value} onClick={() => setFilter(value)}>
                {value === "active" ? "Active" : value === "away" ? "Away" : "Deceased"}
                <small>{value === "active" ? population.home : value === "away" ? population.away : population.deceased}</small>
              </button>
            ))}
          </div>
          <label><span className="visually-hidden">Search survivors</span><input type="search" placeholder="Search people" value={search} onChange={(event) => setSearch(event.currentTarget.value)} /></label>
          <label><span className="visually-hidden">Sort survivors</span><select aria-label="Sort survivors" value={sort} onChange={(event) => setSort(event.currentTarget.value as "name" | "health" | "skill")}><option value="name">Name</option><option value="health">Health</option><option value="skill">Best skill</option></select></label>
        </div>
        <div className="people-page" aria-live="polite">
        {visiblePeople.map((person) => {
          const personActivity = survivorActivityLabel(state, person);
          return (
            <button key={person.id} aria-pressed={person.id === survivor.id} className={`${person.id === survivor.id ? "selected" : ""} ${!person.alive ? "dead" : ""}`} onClick={() => { onSelect(person.id); setChooserOpen(false); }} title={personActivity}>
              <SurvivorPortrait survivor={person} detail="thumb" />
              <span><strong>{person.name}</strong><small>{person.role}</small></span>
              <em>{personActivity}</em>
            </button>
          );
        })}
        {!visiblePeople.length && <p className="quiet-state">No people match this view.</p>}
        </div>
        {totalPages > 1 && (
          <nav className="people-pagination" aria-label="Survivor pages">
            <button type="button" disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</button>
            <span>Page {page} of {totalPages}</span>
            <button type="button" disabled={page === totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>Next</button>
          </nav>
        )}
        <section
          className={`people-recruitment-cta ${!recruitmentAvailable ? "cooldown" : ""}`}
          aria-labelledby="people-recruitment-title"
        >
          <header>
            <UserPlus aria-hidden="true" />
            <span>
              <small>Finding others</small>
              <strong id="people-recruitment-title">People beyond the wire</strong>
            </span>
          </header>
          {state.recruitment && (
            <p className="people-recruitment-record">
              Campaign rescues · {state.recruitment.rescued}{legacyFiniteRecruitment ? `/${state.recruitment.limit}` : " · uncapped"}
            </p>
          )}
          <p>
            {legacyFiniteRecruitment && !recruitmentAvailable
              ? "This community has reached its campaign rescue limit."
              : !recruitmentAvailable
                ? `A new arrival is settling in. Another person can join after ${Math.ceil((state.recruitment?.cooldown_seconds_remaining ?? 0) / 60)} minutes.`
                : "There is no survivor cap. New people are found only through rare, uncertain expedition rescues. Nothing is confirmed before the team returns; housing, provisions and useful work set the practical pace."}
          </p>
          {onOpenSurface && !(legacyFiniteRecruitment && !recruitmentAvailable) && (
            <button type="button" onClick={onOpenSurface}>
              <MapPinned aria-hidden="true" />
              Plan a Surface expedition
              <ArrowRight aria-hidden="true" />
            </button>
          )}
        </section>
        </div>
      </details>
      <article className="person-dossier panel-surface">
        <header>
          <div className="dossier-art">
            <SurvivorPortrait survivor={survivor} className="dossier-portrait" decorative={false} priority />
            <SurvivorFigure survivor={survivor} activity={presentation.activity} className="dossier-figure" facing={presentation.facing} motion={presentation.motion} prop={presentation.prop} />
          </div>
          <div><span className="eyebrow">Survivor file / {survivor.pronouns}</span><h2>{survivor.name}</h2><p>{survivor.role}</p></div>
          <span className={`status-chip status-${survivor.status}`}>{survivor.alive ? survivor.status : "deceased"}</span>
        </header>
        <section className="survivor-work-state" aria-label={`${survivor.name} work status`}>
          <div className="survivor-activity">
            <ActivityIcon aria-hidden="true" />
            <span><small>Current activity</small><strong>{activityLabel}</strong>{activity.kind === "rest" && activity.rest_eta_seconds != null && <em>Estimated return in {formatDuration(activity.rest_eta_seconds)}</em>}</span>
          </div>
          <div className="survivor-duty"><small>Preferred duty</small><strong>{dutyName}</strong></div>
          <label className="relief-eligibility-toggle">
            <input
              type="checkbox"
              checked={reliefEligible}
              disabled={busy || !survivor.alive}
              onChange={() => onCommand({ type: "set_survivor_work_preferences", survivor_id: survivor.id, relief_eligible: !reliefEligible })}
            />
            <span><strong>Eligible for relief duty</strong><small>May temporarily cover higher-priority posts, then returns automatically.</small></span>
          </label>
        </section>
        <div className="dossier-needs">
          <StatusDial value={survivor.health} label="Health" tone="green" />
          <StatusDial value={survivor.morale} label="Morale" tone="amber" />
          <StatusDial value={100 - survivor.fatigue} label="Rested" tone="blue" />
          <StatusDial value={100 - survivor.hunger} label="Fed" tone="red" />
        </div>
        <div className="dossier-columns">
          <section>
            <h3><Brain size={15} />Capabilities</h3>
            <div className="skill-list">{(Object.entries(survivor.skills) as Array<[SkillKey, number]>).sort((a, b) => b[1] - a[1]).map(([skill, rating]) => <div key={skill}><span>{skillLabels[skill]}</span><i>{[1, 2, 3, 4, 5].map((pip) => <b className={pip <= rating ? "filled" : ""} key={pip} />)}</i><em>{rating}</em></div>)}</div>
          </section>
          <section>
            <h3><Sparkles size={15} />What defines them</h3>
            <div className="trait-list">{survivor.traits.map((trait) => <span key={trait}><b>{trait}</b><small>{catalog.traits[trait]?.description ?? "A personal history that shapes how they work."}</small></span>)}</div>
            <h3><Heart size={15} />Wounds & burdens</h3>
            {survivor.injuries.length ? <ul className="injury-list">{survivor.injuries.map((injury, index) => <li key={`${injury.name}-${index}`}><HeartPulse size={13} />{injury.name}<em aria-label={`Severity ${injury.severity} out of 100`}>{injury.severity}</em></li>)}</ul> : <p className="quiet-state">No current physical injuries. That does not mean they are fine.</p>}
            {needsTreatment && (
              <section
                className="survivor-treatment-guidance"
                aria-label={`Treatment for ${survivor.name}`}
              >
                <HeartPulse aria-hidden="true" />
                <p>{treatmentGuidance}</p>
                {infirmary && onOpenInfirmary && (
                  <button type="button" onClick={() => onOpenInfirmary(infirmary.id)}>
                    Open Infirmary controls
                    <ArrowRight aria-hidden="true" />
                  </button>
                )}
              </section>
            )}
            <h3><Backpack size={15} />Carried equipment</h3>
            <EquipmentManager survivor={survivor} state={state} catalog={catalog} busy={busy} onCommand={onCommand} />
          </section>
        </div>
        <section className="connections-panel">
          <h3><Link2 size={15} />Connections</h3>
          <div className="connection-list">{connections.map((connection) => <article key={connection.id}>{connection.other && <SurvivorPortrait survivor={connection.other} detail="thumb" />}<div><strong>{connection.other?.name}</strong><small>{connection.bond >= 40 ? "Bonded" : connection.bond >= 15 ? "Trusting" : connection.tension >= 30 ? "Strained" : "Guarded"}</small></div><i><b style={{ width: `${Math.max(4, connection.bond)}%` }} /></i><em>{connection.trust} trust</em></article>)}</div>
          <h3><History size={15} />What they remember</h3>
          <div className="memory-list">{memories.map((memory, index) => <p key={`${memory.game_time}-${index}`}><span>{memory.kind.replaceAll("_", " ")}</span>{memory.text}<small>{memory.connection}</small></p>)}{!memories.length && <p className="quiet-state">They have survived together, but the defining moments are still ahead.</p>}</div>
        </section>
        <footer className="dossier-footer"><UserRound size={14} /><span>Preferred duty</span><strong>{dutyName}</strong><Moon size={14} /><span>Fatigue</span><strong>{survivor.fatigue}%</strong><Utensils size={14} /><span>Hunger</span><strong>{survivor.hunger}%</strong></footer>
      </article>
    </section>
  );
}
